import { Alert, Box, CircularProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { iframeResizer } from 'iframe-resizer'
import { debounce } from 'lodash'
import { useTranslate } from 'ra-core'
import { useEffect, useRef } from 'react'

import { useApi } from '../api/apiProvider'

const DEFAULT_HEIGHT = 400
const REFRESH_MARGIN_MS = 30 * 1000
const RESIZE_DEBOUNCE_MS = 1000

const MetabaseEmbed = ({ path, params, title }) => {
  const translate = useTranslate()
  const theme = useTheme()
  const iframeRef = useRef(null)
  const [fetchEmbed, { data, error }] = useApi(path, { params })

  useEffect(() => {
    fetchEmbed()
  }, [fetchEmbed])

  useEffect(() => {
    if (!data?.expires_at) return
    const delay = new Date(data.expires_at).getTime() - Date.now() - REFRESH_MARGIN_MS
    const timeout = setTimeout(() => fetchEmbed(), Math.max(delay, 0))
    return () => clearTimeout(timeout)
  }, [data, fetchEmbed])

  const embedOrigin = data?.url ? new URL(data.url).origin : null
  useEffect(() => {
    const iframe = iframeRef.current
    if (!embedOrigin || !iframe) return
    iframeResizer({ checkOrigin: [embedOrigin] }, iframe)
    const remeasure = debounce(() => {
      iframe.style.height = `${DEFAULT_HEIGHT}px`
      iframe.iFrameResizer?.resize()
    }, RESIZE_DEBOUNCE_MS)
    window.addEventListener('resize', remeasure)
    return () => {
      window.removeEventListener('resize', remeasure)
      remeasure.cancel()
      iframe.iFrameResizer?.removeListeners()
    }
  }, [embedOrigin])

  if (error) {
    return <Alert severity="info">{translate('mymove.statistics.unavailable')}</Alert>
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      src={theme.palette.mode === 'dark' ? `${data.url}&theme=night` : data.url}
      title={title}
      width="100%"
      height={DEFAULT_HEIGHT}
      style={{ border: 0, display: 'block' }}
    />
  )
}

export default MetabaseEmbed
